import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_EDITOR_SETTINGS } from '../editor/codemirrorConfig';

const AUTOCOMPLETE_MODES = ['off', 'manual', 'typing'];

function ProfileRow({ id, label, required, children }) {
  return (
    <div className="profile-form-row">
      <label
        htmlFor={id}
        className={`profile-field-label${required ? ' profile-field-label--required' : ''}`}
        title={required ? 'Обязательное поле' : undefined}
      >
        {required ? (
          <>
            <span className="profile-required-mark" aria-hidden="true">
              *
            </span>{' '}
          </>
        ) : null}
        {label}
      </label>
      <div className="profile-field-control">{children}</div>
    </div>
  );
}

export default function ProfilePage() {
  const { refreshUser, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editorSettings, setEditorSettings] = useState(null);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorError, setEditorError] = useState('');
  const [editorSuccess, setEditorSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [parentInvite, setParentInvite] = useState(null);
  const [parentInviteLoading, setParentInviteLoading] = useState(false);
  const [parentInviteError, setParentInviteError] = useState('');
  const [parentInviteHint, setParentInviteHint] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  async function loadProfile() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/auth/profile/');
      setProfile(res.data);
      const raw = res.data?.editor_settings;
      const merged = {
        ...DEFAULT_EDITOR_SETTINGS,
        taskTextWrapping: true,
        ...(raw && typeof raw === 'object' ? raw : {}),
      };
      // новые настройки для WorkPage (scale), чтобы всегда были числа
      const st = Number(merged.workStatementFontScale);
      const sb = Number(merged.workSidebarFontScale);
      merged.workStatementFontScale = Number.isFinite(st) ? st : 1;
      merged.workSidebarFontScale = Number.isFinite(sb) ? sb : 1;
      if (!AUTOCOMPLETE_MODES.includes(merged.autocompleteMode)) {
        merged.autocompleteMode = DEFAULT_EDITOR_SETTINGS.autocompleteMode;
      }
      setEditorSettings(merged);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось загрузить профиль');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!profile) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        middle_name: profile.middle_name || '',
        email: profile.email || '',
        city: profile.city || '',
        school: profile.school || '',
        grade: profile.grade || null,
        contact_channel: profile.contact_channel || 'email',
        phone: profile.phone || '',
        telegram_username: profile.telegram_username || '',
        notifications_consent: profile.notifications_consent !== false,
        is_teacher: !!profile.is_teacher,
        is_parent: !!profile.is_parent,
        is_student: !!profile.is_student,
      };
      const res = await api.patch('/auth/profile/', payload);
      setProfile(res.data);
      await refreshUser();
      setSuccess('Профиль сохранён');
    } catch (e) {
      const msg = e.response?.data;
      if (typeof msg === 'object' && msg !== null) {
        const first = Object.entries(msg)[0];
        setError(first ? `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}` : 'Ошибка сохранения профиля');
      } else {
        setError('Ошибка сохранения профиля');
      }
    } finally {
      setSaving(false);
    }
  }

  function updateField(field, value) {
    setProfile((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  function updateEditorField(field, value) {
    setEditorSettings((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  async function saveEditorSettings(e) {
    e.preventDefault();
    if (!editorSettings) return;
    setEditorSaving(true);
    setEditorError('');
    setEditorSuccess('');
    try {
      await api.patch('/auth/profile/', { editor_settings: editorSettings });
      await refreshUser();
      setEditorSuccess('Настройки редактора сохранены');
      setTimeout(() => setEditorSuccess(''), 2000);
    } catch (e2) {
      const msg = e2?.response?.data;
      setEditorError(typeof msg === 'string' ? msg : (msg?.detail || 'Не удалось сохранить настройки редактора'));
    } finally {
      setEditorSaving(false);
    }
  }

  async function loadParentInvite({ rotate = false } = {}) {
    setParentInviteLoading(true);
    setParentInviteError('');
    setParentInviteHint('');
    try {
      const res = await api.post('/auth/parent-invite/', rotate ? { rotate: true } : {});
      setParentInvite(res.data);
      if (rotate) setParentInviteHint('Ссылка обновлена. Старая больше не работает.');
    } catch (e) {
      setParentInviteError(e.response?.data?.detail || 'Не удалось получить ссылку');
      setParentInvite(null);
    } finally {
      setParentInviteLoading(false);
    }
  }

  async function copyParentInvite() {
    const url = parentInvite?.url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setParentInviteHint('Ссылка скопирована.');
    } catch {
      setParentInviteHint(url);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка профиля...</p>
      </div>
    );
  }

  if (!profile) {
    return <p className="text-secondary">Профиль недоступен.</p>;
  }

  return (
    <div className="profile-page">
      <div className="page-header">
        <h1>Профиль</h1>
        <p className="page-subtitle">Настройки аккаунта и роли на сайте.</p>
      </div>

      {user?.is_student && (
        <div className="questionnaire-profile-card">
          <div>
            <h2 style={{ margin: 0 }}>Анкета ученика</h2>
            <p className="text-secondary" style={{ marginTop: '0.35rem', marginBottom: 0 }}>
              {user.has_questionnaire
                ? 'Можно изменить ответы — преподаватель увидит актуальную версию.'
                : 'Пока не заполнена. От неё зависят режим занятий и объём ДЗ. Можно заполнить в любой момент.'}
            </p>
          </div>
          <Link
            to="/questionnaire"
            className={`btn ${user.has_questionnaire ? 'btn-outline' : 'btn-primary'}`}
          >
            {user.has_questionnaire ? 'Редактировать анкету' : 'Заполнить анкету'}
          </Link>
        </div>
      )}

      {user?.is_student && (
        <div className="questionnaire-profile-card" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <div>
            <h2 style={{ margin: 0 }}>Пригласить родителя</h2>
            <p className="text-secondary" style={{ marginTop: '0.35rem' }}>
              Секретная ссылка: родитель зарегистрируется или войдёт, примет договор-оферту
              и привяжется к вашему аккаунту. Можно отправить маме и папе одну и ту же ссылку.
              «Обновить» делает старую ссылку недействительной.
            </p>
          </div>
          {parentInviteError && <div className="alert alert-error">{parentInviteError}</div>}
          {parentInviteHint && <div className="alert alert-success">{parentInviteHint}</div>}
          {parentInvite?.url ? (
            <div className="invite-link-row">
              <code className="invite-url">{parentInvite.url}</code>
              <button type="button" className="btn btn-xs btn-outline" onClick={copyParentInvite}>
                Копировать
              </button>
              <button
                type="button"
                className="btn btn-xs btn-outline"
                disabled={parentInviteLoading}
                onClick={() => loadParentInvite({ rotate: true })}
              >
                Обновить
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              style={{ alignSelf: 'flex-start' }}
              disabled={parentInviteLoading}
              onClick={() => loadParentInvite()}
            >
              {parentInviteLoading ? 'Загрузка…' : 'Получить ссылку'}
            </button>
          )}
          {parentInvite?.expires_at && (
            <p className="text-secondary" style={{ marginBottom: 0, marginTop: '0.5rem' }}>
              Действует до {new Date(parentInvite.expires_at).toLocaleDateString('ru-RU')}
            </p>
          )}
        </div>
      )}

      <form className="profile-form" onSubmit={handleSave}>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="profile-form-fields">
          <ProfileRow id="profile-id" label="ID профиля">
            <span id="profile-id" className="profile-readonly-value">
              {profile.id}
            </span>
          </ProfileRow>
          <ProfileRow id="profile-username" label="Логин">
            <span id="profile-username" className="profile-readonly-value">
              {profile.username || '—'}
            </span>
          </ProfileRow>
          <ProfileRow id="profile-first_name" label="Имя" required>
            <input
              id="profile-first_name"
              type="text"
              className="form-input"
              value={profile.first_name || ''}
              onChange={(e) => updateField('first_name', e.target.value)}
            />
          </ProfileRow>
          <ProfileRow id="profile-last_name" label="Фамилия">
            <input
              id="profile-last_name"
              type="text"
              className="form-input"
              value={profile.last_name || ''}
              onChange={(e) => updateField('last_name', e.target.value)}
            />
          </ProfileRow>
          <ProfileRow id="profile-middle_name" label="Отчество">
            <input
              id="profile-middle_name"
              type="text"
              className="form-input"
              value={profile.middle_name || ''}
              onChange={(e) => updateField('middle_name', e.target.value)}
            />
          </ProfileRow>
          <ProfileRow id="profile-email" label="Email" required>
            <input
              id="profile-email"
              type="email"
              className="form-input"
              value={profile.email || ''}
              onChange={(e) => updateField('email', e.target.value)}
            />
          </ProfileRow>
          <ProfileRow id="profile-city" label="Город">
            <input
              id="profile-city"
              type="text"
              className="form-input"
              value={profile.city || ''}
              onChange={(e) => updateField('city', e.target.value)}
            />
          </ProfileRow>
          <ProfileRow id="profile-school" label="Школа">
            <input
              id="profile-school"
              type="text"
              className="form-input"
              value={profile.school || ''}
              onChange={(e) => updateField('school', e.target.value)}
            />
          </ProfileRow>
          <ProfileRow id="profile-grade" label="Класс">
            <input
              id="profile-grade"
              type="number"
              className="form-input"
              value={profile.grade ?? ''}
              onChange={(e) => updateField('grade', e.target.value ? parseInt(e.target.value, 10) : null)}
            />
          </ProfileRow>
          <ProfileRow id="profile-contact_channel" label="Канал связи">
            <select
              id="profile-contact_channel"
              className="form-select"
              value={profile.contact_channel || 'email'}
              onChange={(e) => updateField('contact_channel', e.target.value)}
            >
              <option value="email">Email</option>
              <option value="max">Макс</option>
              <option value="phone">Телефон</option>
              {profile.contact_channel === 'telegram' && (
                <option value="telegram">Telegram</option>
              )}
            </select>
          </ProfileRow>
          <ProfileRow id="profile-notifications_consent" label="Уведомления">
            <label className="checkbox-row" style={{ margin: 0 }}>
              <input
                id="profile-notifications_consent"
                type="checkbox"
                checked={profile.notifications_consent !== false}
                onChange={(e) => updateField('notifications_consent', e.target.checked)}
              />
              <span>Служебные пинги: ссылка на урок, перенос, сообщения на сайте</span>
            </label>
          </ProfileRow>
          {profile.contact_channel === 'max' && (
            <ProfileRow id="profile-max-bind" label="Бот Макс">
              <div className="muted">
                {profile.max_chat_id
                  ? `Привязан (chat_id: ${profile.max_chat_id}).`
                  : 'Пока не привязан. Напишите боту Макс сообщение: /start ваш_логин — тогда пинги пойдут в Макс.'}
              </div>
            </ProfileRow>
          )}
          <ProfileRow id="profile-phone" label="Телефон">
            <input
              id="profile-phone"
              type="text"
              className="form-input"
              value={profile.phone || ''}
              onChange={(e) => updateField('phone', e.target.value)}
            />
          </ProfileRow>
        </div>

        <div className="profile-roles form-group">
          <span className="profile-roles-heading">Роль на сайте</span>
          <div className="checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={!!profile.is_teacher}
                onChange={(e) => updateField('is_teacher', e.target.checked)}
              />
              Я преподаватель
            </label>
            <label>
              <input
                type="checkbox"
                checked={!!profile.is_parent}
                onChange={(e) => updateField('is_parent', e.target.checked)}
              />
              Я родитель
            </label>
            <label>
              <input
                type="checkbox"
                checked={!!profile.is_student}
                onChange={(e) => updateField('is_student', e.target.checked)}
              />
              Я ученик
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </form>

      <div
        className="profile-editor-settings"
        style={{
          marginTop: '1rem',
          padding: '0.75rem',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          background: 'rgba(255,255,255,0.02)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0 }}>Редактор</h2>
            <p className="text-secondary" style={{ marginTop: '0.25rem', marginBottom: 0 }}>
              Настройки редактора кода и отображения на странице работы.
            </p>
          </div>
        </div>

        <form onSubmit={saveEditorSettings} style={{ marginTop: '0.75rem' }}>
          {editorError && <div className="alert alert-error">{editorError}</div>}
          {editorSuccess && <div className="alert alert-success">{editorSuccess}</div>}

          <div className="profile-form-fields">
            <ProfileRow id="editor-fontSizePx" label="Размер шрифта кода (px)">
              <input
                id="editor-fontSizePx"
                type="number"
                className="form-input"
                min={10}
                max={24}
                value={editorSettings?.fontSizePx ?? DEFAULT_EDITOR_SETTINGS.fontSizePx}
                onChange={(e) => updateEditorField('fontSizePx', e.target.value ? Number(e.target.value) : DEFAULT_EDITOR_SETTINGS.fontSizePx)}
              />
            </ProfileRow>

            <ProfileRow id="work-statement-scale" label="Размер текста условия (WorkPage)">
              <input
                id="work-statement-scale"
                type="number"
                className="form-input"
                min={0.8}
                max={1.4}
                step={0.05}
                value={editorSettings?.workStatementFontScale ?? 1}
                onChange={(e) => updateEditorField('workStatementFontScale', e.target.value ? Number(e.target.value) : 1)}
              />
            </ProfileRow>

            <ProfileRow id="work-sidebar-scale" label="Размер текста сайдбара (WorkPage)">
              <input
                id="work-sidebar-scale"
                type="number"
                className="form-input"
                min={0.75}
                max={1.3}
                step={0.05}
                value={editorSettings?.workSidebarFontScale ?? 1}
                onChange={(e) => updateEditorField('workSidebarFontScale', e.target.value ? Number(e.target.value) : 1)}
              />
            </ProfileRow>

            <ProfileRow id="editor-lineHeight" label="Интерлиньяж">
              <input
                id="editor-lineHeight"
                type="number"
                className="form-input"
                min={1.1}
                max={2.2}
                step={0.05}
                value={editorSettings?.lineHeight ?? DEFAULT_EDITOR_SETTINGS.lineHeight}
                onChange={(e) => updateEditorField('lineHeight', e.target.value ? Number(e.target.value) : DEFAULT_EDITOR_SETTINGS.lineHeight)}
              />
            </ProfileRow>

            <ProfileRow id="editor-autocomplete" label="Автодополнение">
              <select
                id="editor-autocomplete"
                className="form-select"
                value={AUTOCOMPLETE_MODES.includes(editorSettings?.autocompleteMode) ? editorSettings.autocompleteMode : 'manual'}
                onChange={(e) => updateEditorField('autocompleteMode', e.target.value)}
              >
                <option value="off">Выключено</option>
                <option value="manual">Только по Ctrl+Space</option>
                <option value="typing">Автоматически при наборе</option>
              </select>
              <p className="text-secondary" style={{ fontSize: '0.85rem', marginTop: '0.35rem', marginBottom: 0 }}>
                Всплывающий список шаблонов кода (например «if»). Галочка «Подсказки к задаче» ниже — это текст от автора, не этот список.
              </p>
            </ProfileRow>
          </div>

          <div className="form-group" style={{ marginTop: '0.75rem' }}>
            <div
              className="checkbox-row"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '0.35rem 0.75rem',
                alignItems: 'start',
                fontSize: '0.9rem',
              }}
            >
              <label><input type="checkbox" checked={!!editorSettings?.closeBrackets} onChange={(e) => updateEditorField('closeBrackets', e.target.checked)} /> Автозакрытие скобок</label>
              <label><input type="checkbox" checked={!!editorSettings?.bracketMatching} onChange={(e) => updateEditorField('bracketMatching', e.target.checked)} /> Подсветка парных скобок</label>
              <label><input type="checkbox" checked={!!editorSettings?.showHints} onChange={(e) => updateEditorField('showHints', e.target.checked)} /> Подсказки к задаче</label>
              <label><input type="checkbox" checked={!!editorSettings?.lineWrapping} onChange={(e) => updateEditorField('lineWrapping', e.target.checked)} /> Перенос длинных строк</label>
              <label><input type="checkbox" checked={editorSettings?.taskTextWrapping !== false} onChange={(e) => updateEditorField('taskTextWrapping', e.target.checked)} /> Перенос текста задачи</label>
              <label><input type="checkbox" checked={!!editorSettings?.lineNumbers} onChange={(e) => updateEditorField('lineNumbers', e.target.checked)} /> Номера строк</label>
              <label><input type="checkbox" checked={!!editorSettings?.showSearch} onChange={(e) => updateEditorField('showSearch', e.target.checked)} /> Поиск/замена (Ctrl+F)</label>
              <label><input type="checkbox" checked={!!editorSettings?.highlightSelectionMatches} onChange={(e) => updateEditorField('highlightSelectionMatches', e.target.checked)} /> Подсветка вхождений</label>
              <label><input type="checkbox" checked={!!editorSettings?.highlightTrailingWhitespace} onChange={(e) => updateEditorField('highlightTrailingWhitespace', e.target.checked)} /> Хвостовые пробелы</label>
              <label><input type="checkbox" checked={!!editorSettings?.highlightActiveLine} onChange={(e) => updateEditorField('highlightActiveLine', e.target.checked)} /> Текущая строка</label>
              <label><input type="checkbox" checked={!!editorSettings?.rulersEnabled} onChange={(e) => updateEditorField('rulersEnabled', e.target.checked)} /> Линейка (80)</label>
              <label><input type="checkbox" checked={!!editorSettings?.indentGuides} onChange={(e) => updateEditorField('indentGuides', e.target.checked)} /> Направляющие отступов</label>
              <label><input type="checkbox" checked={editorSettings?.autosaveDrafts !== false} onChange={(e) => updateEditorField('autosaveDrafts', e.target.checked)} /> Автосохранение черновика</label>
              <label><input type="checkbox" checked={!!editorSettings?.ligatureFont} onChange={(e) => updateEditorField('ligatureFont', e.target.checked)} /> Шрифт с лигатурами (&lt;= и == как один символ)</label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={editorSaving || !editorSettings}>
              {editorSaving ? 'Сохранение...' : 'Сохранить настройки редактора'}
            </button>
          </div>
        </form>
      </div>

      {!user?.is_staff && (
        <div className="delete-account-profile-block">
          <h2 style={{ margin: 0 }}>Удаление аккаунта</h2>
          <p className="text-secondary" style={{ marginTop: '0.35rem' }}>
            Можно запросить безвозвратное удаление персональных данных.
            При ненулевом балансе сначала нужно погасить долг или вывести деньги.
          </p>
          <Link to="/profile/delete" className="btn btn-outline btn-danger-outline">
            Удалить аккаунт навсегда
          </Link>
        </div>
      )}
    </div>
  );
}
